import React, { useState, useEffect, useCallback } from "react";
import ProgressFullBar from "../ProgressFullBar/ProgressFullBar";
import "./ProductOne.scss";
import { useDispatch, useSelector } from "react-redux";
import { Countries } from "../../../../../api/CountryApi";
import Select from "react-select";
import {
    getAllCategories,
    getAllCategoriesWithChildren,
} from "../../../../../api/CategoryApi/CategorySlice.js";
import { getallLang } from "../../../../../api/LanguageApi/Lang";
import RichTextEditor from "../../../../../Components/RichTextEditor/RichTextEditor";

/* ── Language meta ─────────────────────────────────────────────────── */
const LANG_LABELS = {
    1: "Arabic  (العربية)",
    2: "English",
    3: "Italian (Italiano)",
};

/* ── Flatten nested category tree into leaves ──────────────────────── */
const flattenLeaves = (tree = []) => {
    const leaves = [];
    tree.forEach((root) => {
        if (root?.children?.length) {
            root.children.forEach((mid) => {
                if (mid?.children?.length) {
                    mid.children.forEach((leaf) => leaves.push(leaf));
                } else {
                    leaves.push(mid);
                }
            });
        } else {
            leaves.push(root);
        }
    });
    return leaves;
};

/* ── Common spec label suggestions ─────────────────────────────────── */
const SPEC_SUGGESTIONS = [
    "Capacity", "Watt", "Ampere", "Voltage", "Liter",
    "Weight", "Dimensions", "Material", "Color", "Speed",
    "Pressure", "Temperature", "Frequency", "Power", "Bar",
];

/* ── Single spec row ────────────────────────────────────────────────── */
const SpecRow = ({ spec, index, onChange, onRemove, isOnly }) => (
    <div style={specStyles.row}>
        <div style={specStyles.indexBadge}>{index + 1}</div>

        <div style={specStyles.inputWrap}>
            <input
                list="spec-suggestions"
                placeholder="e.g. Capacity, Watt..."
                value={spec.label}
                onChange={(e) => onChange(index, "label", e.target.value)}
                style={specStyles.input}
            />
        </div>

        <span style={specStyles.colon}>:</span>

        <div style={specStyles.inputWrap}>
            <input
                placeholder="e.g. 1.2L, 1500W..."
                value={spec.value}
                onChange={(e) => onChange(index, "value", e.target.value)}
                style={specStyles.input}
            />
        </div>

        <button
            type="button"
            onClick={() => onRemove(index)}
            style={{ ...specStyles.removeBtn, visibility: isOnly ? "hidden" : "visible" }}
            title="Remove"
        >
            ✕
        </button>
    </div>
);

/* ── Main component ─────────────────────────────────────────────────── */
const AddProductOne = ({
                           onNext,
                           name,            setName,
                           category,        setCategory,
                           setDesc,         desc,
                           seo,             setSeo,
                           productLocation, setProductLocation,
                           modelNum,        setModelNum,
                           isActive,        setIsActive,
                           featured,        setFeatured,
                           selectedOption,  setSelectedOption,
                           specs,           setSpecs,   /* ← add these two props in the parent */
                       }) => {
    const dispatch = useDispatch();

    useEffect(() => { dispatch(getAllCategoriesWithChildren()); }, [dispatch]);
    useEffect(() => { dispatch(getallLang()); },                  [dispatch]);

    const data  = useSelector((state) => state?.category?.categoryWithChild?.data);
    const langs = useSelector((state) => state?.Lang?.lang?.langs);

    /* ── Category grouping ── */
    const allLeaves     = flattenLeaves(data);
    const grouped       = allLeaves.reduce((acc, item) => {
        const langId = item?.language_id ?? 0;
        if (!acc[langId]) acc[langId] = [];
        acc[langId].push(item);
        return acc;
    }, {});
    const sortedLangIds = Object.keys(grouped)
        .map(Number)
        .sort((a, b) => {
            const order = [1, 2, 3];
            const ai = order.indexOf(a), bi = order.indexOf(b);
            if (ai === -1 && bi === -1) return a - b;
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
        });

    /* ── Spec state — local fallback if parent doesn't supply specs/setSpecs ── */
    const [localSpecs, setLocalSpecs]   = useState([{ label: "", value: "" }]);
    const activeSpecs                   = specs    ?? localSpecs;
    const activeSetSpecs                = setSpecs ?? setLocalSpecs;

    const handleSpecChange = useCallback((index, field, val) => {
        activeSetSpecs((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: val };
            return updated;
        });
    }, [activeSetSpecs]);

    const addSpec    = useCallback(() => {
        activeSetSpecs((prev) => [...prev, { label: "", value: "" }]);
    }, [activeSetSpecs]);

    const removeSpec = useCallback((index) => {
        activeSetSpecs((prev) => prev.filter((_, i) => i !== index));
    }, [activeSetSpecs]);

    const options = langs?.map((l) => ({ value: l.id, label: l.name }));

    const hasPreview = activeSpecs.some((s) => s.label || s.value);

    return (
        <div>
            <ProgressFullBar showImage={false} showSpec={false} completed={50} />

            <div className="AddProductForm">

                {/* Product Name */}
                <div className="FormName">
                    <span>Product Name</span>
                    <input type="text" placeholder="Enter Product Name" value={name}
                           onChange={(e) => setName(e.target.value)} />
                </div>

                {/* SEO */}
                <div className="FormName">
                    <span>SEO</span>
                    <input type="text" placeholder="Enter SEO" value={seo}
                           onChange={(e) => setSeo(e.target.value)} />
                </div>

                {/* Category */}
                <div className="selectForm">
                    <label htmlFor="category">Category</label>
                    <select id="category" onChange={(e) => setCategory([Number(e.target.value)])} defaultValue="">
                        <option value="" disabled>Select Your Category</option>
                        {sortedLangIds.map((langId) => (
                            <optgroup key={langId} label={`── ${LANG_LABELS[langId] ?? `Language ${langId}`} ──`}>
                                {grouped[langId].map((item) => (
                                    <option key={item?.id} value={item?.id}>{item?.name}</option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                </div>

                {/* Country */}
                <div className="selectForm">
                    <label htmlFor="Country">Country</label>
                    <select id="Country" value={productLocation} onChange={(e) => setProductLocation(e.target.value)}>
                        <option value="">Select Country</option>
                        {Countries?.map((item, i) => (
                            <option key={i} value={item?.code}>{item.name}</option>
                        ))}
                    </select>
                </div>

                {/* Description */}
                <div className="FormName">
                    <span>Description</span>
                    <RichTextEditor value={desc} onChange={setDesc} placeholder="Enter Product Description" />
                </div>

                {/* Model Number */}
                <div className="FormName">
                    <span>Model No.</span>
                    <input type="text" placeholder="Enter Model Number"
                           onChange={(e) => setModelNum(e.target.value)} />
                </div>

                {/* ── Product Specifications ──────────────────────────────────── */}
                <div className="FormName">
                    <span>Product Specifications</span>
                    <p style={specStyles.hint}>
                        Add technical specs displayed on the product card (Capacity, Watt, Ampere, etc.)
                    </p>

                    {/* Autocomplete suggestions */}
                    <datalist id="spec-suggestions">
                        {SPEC_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                    </datalist>

                    {/* Column headers */}
                    <div style={specStyles.headers}>
                        <div style={{ width: 28 }} />
                        <div style={{ ...specStyles.headerLabel, flex: 1 }}>Spec Name</div>
                        <div style={{ width: 20 }} />
                        <div style={{ ...specStyles.headerLabel, flex: 1 }}>Value</div>
                        <div style={{ width: 30 }} />
                    </div>

                    {/* Rows */}
                    <div style={specStyles.list}>
                        {activeSpecs.map((spec, i) => (
                            <SpecRow
                                key={i}
                                spec={spec}
                                index={i}
                                onChange={handleSpecChange}
                                onRemove={removeSpec}
                                isOnly={activeSpecs.length === 1}
                            />
                        ))}
                    </div>

                    {/* Add row */}
                    <button type="button" onClick={addSpec} style={specStyles.addBtn}>
                        + Add Specification
                    </button>

                    {/* Live preview — only shown when at least one spec has content */}
                    {hasPreview && (
                        <div style={specStyles.preview}>
                            <p style={specStyles.previewTitle}>Card Preview</p>
                            <div style={specStyles.previewGrid}>
                                {activeSpecs
                                    .filter((s) => s.label || s.value)
                                    .map((s, i) => (
                                        <div key={i} style={specStyles.previewChip}>
                                            <span style={specStyles.previewKey}>{s.label || "—"}</span>
                                            <span style={specStyles.previewVal}>{s.value || "—"}</span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
                {/* ── End Specifications ─────────────────────────────────────── */}

                {/* Language */}
                <div style={{ width: "10vw" }}>
                    <p>Language</p>
                    <Select defaultValue={selectedOption} onChange={setSelectedOption} options={options} />
                </div>

                <div className="AddProductBtn">
                    <button onClick={onNext}>Next</button>
                </div>

            </div>
        </div>
    );
};

/* ── Spec section styles ─────────────────────────────────────────────── */
const specStyles = {
    hint: {
        margin: "2px 0 10px",
        fontSize: 12,
        color: "#6b7280",
    },
    headers: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 4,
    },
    headerLabel: {
        fontSize: 11,
        fontWeight: 700,
        color: "#374151",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    list: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
    },
    row: {
        display: "flex",
        alignItems: "center",
        gap: 8,
    },
    indexBadge: {
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: "#e5e7eb",
        color: "#6b7280",
        fontSize: 11,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    inputWrap: { flex: 1 },
    input: {
        width: "100%",
        padding: "7px 10px",
        border: "1px solid #d1d5db",
        borderRadius: 6,
        fontSize: 14,
        outline: "none",
        boxSizing: "border-box",
    },
    colon: {
        fontSize: 18,
        color: "#9ca3af",
        width: 12,
        textAlign: "center",
        flexShrink: 0,
    },
    removeBtn: {
        width: 26,
        height: 26,
        borderRadius: "50%",
        border: "none",
        background: "#fee2e2",
        color: "#dc2626",
        cursor: "pointer",
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
    },
    addBtn: {
        marginTop: 10,
        padding: "7px 16px",
        background: "#f0fdf4",
        border: "1px dashed #86efac",
        borderRadius: 6,
        color: "#16a34a",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        width: "100%",
    },
    preview: {
        marginTop: 14,
        padding: "10px 14px",
        background: "#f8f9fa",
        borderRadius: 8,
        border: "1px solid #e5e7eb",
    },
    previewTitle: {
        margin: "0 0 8px",
        fontSize: 11,
        fontWeight: 700,
        color: "#9ca3af",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    previewGrid: {
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
    },
    previewChip: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "6px 14px",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        minWidth: 80,
    },
    previewKey: {
        fontSize: 11,
        color: "#6b7280",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
    },
    previewVal: {
        fontSize: 15,
        fontWeight: 700,
        color: "#111827",
        marginTop: 2,
    },
};

export default AddProductOne;